/// <amd-module name="Wit/WorkItems/Redux/WorkItemReducer" />
import produce from "immer";
import { FieldStatusFlags } from "Wit/Clients/Internal/Contracts/SocialWorkItem";
import { CoreFieldRefNames } from "Wit/Common/CoreFieldRefNames";
import { FieldValueMap } from "Wit/WorkItemTypes/Contracts";
import { transformFieldReferenceNames } from "../Utilities/FieldUtilities";
import { areArtifactsEquivalent, areAttachmentsEquivalent, areLinksEquivalent, mergeArrays } from "../Utilities/WorkItemRelationUtilities";
import { isWorkItemNew } from "../Utilities/WorkItemUtilities";
import { WorkItemActions, WorkItemActionTypes } from "../WorkItemActions";
import { IWorkItem, IWorkItemRelations, IWorkItemState, WorkItemStatus } from "../WorkItemContracts";

const DEFAULT_STATE: IWorkItemState = {
    workItems: {},
    fieldProperties: {}
};

export function workItemsReducer(state: IWorkItemState, action: WorkItemActions): IWorkItemState {
    const currentState = state || DEFAULT_STATE;
    return produce(currentState, draft => {
        switch (action.type) {
            case WorkItemActionTypes.LoadedWorkItems: {
                const { workItems } = action.payload;
                handleWorkItemsLoaded(draft, workItems);
                break;
            }
            case WorkItemActionTypes.RemoveTemporaryWorkItem:
            case WorkItemActionTypes.WorkItemDeleteSucceeded: {
                const { workItemId } = action.payload;
                delete draft.workItems[workItemId];
                delete draft.fieldProperties[workItemId];
                break;
            }
            case WorkItemActionTypes.WorkItemUpdated: {
                const { fields, workItemRelations, workItemId } = action.payload;
                handleWorkItemUpdated(draft, workItemId, fields, workItemRelations);
                break;
            }

            case WorkItemActionTypes.WorkItemChangeRequested: {
                const { workItemId, workItemChange } = action.payload;
                const workItem = draft.workItems[workItemId];

                if (isWorkItemNew(workItemId)) {
                    // In creation mode just update work item fields directly
                    for (const fieldChange of workItemChange.fieldChanges || []) {
                        workItem.fields[fieldChange.fieldRefName.toLocaleLowerCase()] = fieldChange.value;
                    }
                } else {
                    if (!workItem.pendingChanges) {
                        workItem.pendingChanges = [workItemChange];
                    } else {
                        workItem.pendingChanges.push(workItemChange);
                    }
                }

                break;
            }

            case WorkItemActionTypes.WorkItemChangeSucceeded: {
                const { workItemId, workItemChange } = action.payload;
                // Clear allowed values if a field change was successful
                clearAllowedValues(draft, workItemId);

                // Look in the unmodified state to find the matching index, otherwise immer's proxy won't allow us to use reference
                // equality.
                const stateWorkItem = currentState.workItems[workItemId];
                const workItem = draft.workItems[workItemId];
                if (stateWorkItem && workItem && workItem.pendingChanges && stateWorkItem.pendingChanges) {
                    const idx = stateWorkItem.pendingChanges.indexOf(workItemChange);
                    if (idx >= 0) {
                        workItem.pendingChanges.splice(idx, 1);
                        if (workItem.pendingChanges.length === 0) {
                            delete workItem.pendingChanges;
                        }
                    }
                }

                break;
            }

            case WorkItemActionTypes.LoadAllowedValuesSucceeded: {
                const { workItemId, fieldRefName, allowedValues } = action.payload;
                updateAllowedValues(draft, workItemId, fieldRefName, allowedValues);
                break;
            }
            case WorkItemActionTypes.LoadFieldFlagsSucceeded: {
                const { workItemId, fieldFlags } = action.payload;
                updateFieldFlags(draft, workItemId, fieldFlags);
                break;
            }

            case WorkItemActionTypes.UpdateWorkItemsStatus: {
                const payloads = action.payload;
                for (const workItemPayload of payloads) {
                    const { workItemId, status, error } = workItemPayload;
                    updateWorkItemStatus(draft, workItemId, status, error);
                }

                break;
            }
            case WorkItemActionTypes.setWorkItemError: {
                const { workItemId, error } = action.payload;
                setWorkItemError(draft, workItemId, error);
                break;
            }
            case WorkItemActionTypes.clearWorkItemError: {
                const { workItemId } = action.payload;
                setWorkItemError(draft, workItemId, undefined);
                break;
            }
            case WorkItemActionTypes.WorkItemCreateFailed: {
                const { tempWorkItemId, error } = action.payload;
                setWorkItemError(draft, tempWorkItemId, error);
                updateWorkItemStatus(draft, tempWorkItemId, WorkItemStatus.Loaded);
                break;
            }
        }
    });
}

function handleWorkItemsLoaded(draft: IWorkItemState, workItems: IWorkItem[]) {
    for (const workItem of workItems) {
        const storedWorkItem: IWorkItem = (draft.workItems[workItem.id] = {
            fields: {},
            pendingChanges: [],
            projectId: workItem.projectId,
            id: workItem.id,
            workItemTypeName: workItem.workItemTypeName,
            status: WorkItemStatus.Loaded,
            attachments: workItem.attachments,
            artifactLinks: workItem.artifactLinks,
            links: workItem.links
        });

        // Remap fields using lowercase lookup
        for (const fieldRefName of Object.keys(workItem.fields)) {
            storedWorkItem.fields[fieldRefName.toLocaleLowerCase()] = workItem.fields[fieldRefName];
        }

        if (!workItem.workItemTypeName) {
            // Fill out work item type information if present
            const workItemTypeName = storedWorkItem.fields && (storedWorkItem.fields[CoreFieldRefNames.WorkItemType.toLocaleLowerCase()] as string);
            if (workItemTypeName) {
                storedWorkItem.workItemTypeName = workItemTypeName;
            }
        }
    }
}

function handleWorkItemUpdated(draft: IWorkItemState, id: number, fields?: FieldValueMap, workItemRelations?: IWorkItemRelations) {
    const workItem = draft.workItems[id];

    if (!workItem) {
        // Ignore if the work item is not in the store
        return;
    }

    // Update fields
    if (fields) {
        workItem.fields = transformFieldReferenceNames(fields);
    }

    // Update relations
    if (!workItem.links) {
        workItem.links = [];
    }
    if (!workItem.attachments) {
        workItem.attachments = [];
    }
    if (!workItem.artifactLinks) {
        workItem.artifactLinks = [];
    }
    if (workItemRelations) {
        workItemRelations.links &&
            mergeArrays(
                workItemRelations.links,
                workItem.links,
                link => `${link.referenceName}_${link.targetWorkItemId}_${link.targetProjectId}`,
                areLinksEquivalent
            );
        workItemRelations.attachments &&
            mergeArrays(workItemRelations.attachments, workItem.attachments, attachment => `${attachment.id}`, areAttachmentsEquivalent);
        workItemRelations.artifactLinks &&
            mergeArrays(workItemRelations.artifactLinks, workItem.artifactLinks, artifactLink => `${artifactLink.name}`, areArtifactsEquivalent);
    }
}

function updateAllowedValues(draft: IWorkItemState, workItemId: number, fieldRefName: string, allowedValues: string[]) {
    if (!draft.fieldProperties[workItemId]) {
        draft.fieldProperties[workItemId] = { allowedValues: {}, fieldFlags: {} };
    }

    draft.fieldProperties[workItemId].allowedValues[fieldRefName.toLocaleLowerCase()] = allowedValues;
}

function clearAllowedValues(draft: IWorkItemState, workItemId: number) {
    if (draft.fieldProperties[workItemId]) {
        draft.fieldProperties[workItemId].allowedValues = {};
    }
}

function updateFieldFlags(draft: IWorkItemState, workItemId: number, fieldFlags: { [referenceName: string]: FieldStatusFlags }) {
    if (!draft.fieldProperties[workItemId]) {
        draft.fieldProperties[workItemId] = { allowedValues: {}, fieldFlags: {} };
    }

    for (const fieldRefName in fieldFlags) {
        draft.fieldProperties[workItemId].fieldFlags[fieldRefName.toLocaleLowerCase()] = fieldFlags[fieldRefName];
    }
}

function updateWorkItemStatus(draft: IWorkItemState, workItemId: number, status: WorkItemStatus, error?: string) {
    if (!draft.workItems[workItemId]) {
        draft.workItems[workItemId] = { id: workItemId, projectId: "", fields: {}, pendingChanges: [] };
    }

    draft.workItems[workItemId].status = status;

    // 'updateWorkItemStatus' is triggered only when the client has new data from the server (loaded/deleted/updated, etc).
    // So we are expected to clear workItem error if error prop is empty.
    draft.workItems[workItemId].error = error;
}

function setWorkItemError(draft: IWorkItemState, workItemId: number, error?: string) {
    if (draft.workItems[workItemId]) {
        draft.workItems[workItemId].error = error;

        // Clear all pending changes
        draft.workItems[workItemId].pendingChanges = undefined;
    }
}
